import { WebSocketService } from "@frontend-shared/api/websocket";
import {
  ACCESS_TOKEN_STORAGE_KEY,
  AUTH_REFRESH_ENDPOINT,
} from "@frontend-shared/constants";
import axios from "axios";

import { PATHS } from "@/constants/paths";

import { getLanguage, getProjectUuid } from "./auth";
import { flushChatStreamBuffer } from "./chat-stream-buffer";
import { rememberLicenseExpiredReturnPath } from "./license-expired-return-path";
import { localStorageAdapter } from "./storage-adapter";
import { hasActiveStreamProbe } from "./websocket-active-stream-probe";
import {
  consumeE2eForceNextSendMessageThrow,
  getE2eAckTimeoutMsOverride,
  getE2eHealthCheckTimeoutMsOverride,
  getE2eMockSocketServerUrl,
} from "./websocket-e2e-hooks";

// NOTE: NSAI-5117 — the web client used to carry its own 1908-line copy of this
// transport. It now consumes the shared one (apps/nsai/frontend-shared/api/websocket.ts),
// which mobile already used, so a transport fix lands once instead of twice.
// Everything web-specific is injected below.

// NOTE: Relative so the Vite dev proxy handles it; E2E runs point it at the
// Playwright mock Socket.IO server instead.
const CHAT_SOCKET_PATH = "/api/chat";

export const websocketService = WebSocketService.getInstance({
  url: getE2eMockSocketServerUrl() ?? CHAT_SOCKET_PATH,
  getUrl: () => getE2eMockSocketServerUrl() ?? CHAT_SOCKET_PATH,
  storageAdapter: localStorageAdapter,
  getLanguage,
  getProjectUuid,
  // NOTE: The web app refreshes through the REST endpoint directly rather than
  // its axios client, so a 401 interceptor cannot recurse into this call.
  onTokenRefresh: async () => {
    const response = await axios.post(AUTH_REFRESH_ENDPOINT);
    const { accessToken } = response.data;
    if (!accessToken) return null;
    localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, accessToken);
    return accessToken;
  },
  // NOTE: NSAI-5908 — per-token frames are coalesced client-side, so every other
  // socket event has to see them applied first.
  onFlushStreamBuffer: () => flushChatStreamBuffer(),
  // NOTE: NSAI-5117 — keeps a token refresh from tearing down a socket that is
  // mid-reply; the reconnect runs once the stream settles.
  hasActiveStream: hasActiveStreamProbe,
  generateRequestId: () => `${Date.now()}-${crypto.randomUUID()}`,
  beforeSendMessage: () => {
    if (consumeE2eForceNextSendMessageThrow()) {
      throw new Error("forced-send-message-error");
    }
  },
  getAckTimeoutMsOverride: getE2eAckTimeoutMsOverride,
  getHealthCheckTimeoutMsOverride: getE2eHealthCheckTimeoutMsOverride,
  onLicenseExpired: () => {
    if (globalThis.location.pathname === PATHS.LICENSE_EXPIRED) return;
    // NOTE: Save the current path so the license-expired page can restore it
    // on reload.
    rememberLicenseExpiredReturnPath();
    globalThis.location.href = PATHS.LICENSE_EXPIRED;
  },
});

// NOTE: Expose the auth-reconnect trigger to Playwright when the E2E hook object
// is present (set via addInitScript). Guarded so production builds — where the
// property is undefined — never attach it.
if (
  globalThis.window !== undefined &&
  globalThis.window.__NSAI_E2E_WEBSOCKET__
) {
  globalThis.window.__NSAI_E2E_WEBSOCKET__.triggerAuthReconnect = () =>
    websocketService.updateAuthAndReconnect();
}
